import { formatAddress, normalizeUsername } from "@ccnote/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError, json } from "../api";
import { Logo } from "../components/Logo";
import { navigate } from "../router";

const REASONS: Record<string, string> = {
  invalid: "3–30 characters: letters, numbers, dots, dashes or underscores, starting with a letter or number.",
  reserved: "That name is reserved.",
  taken: "That name is already taken.",
};

export function Signup() {
  const qc = useQueryClient();
  const pending = useQuery({
    queryKey: ["signup"],
    queryFn: () => api<{ email: string; displayName: string | null; suggestion: string | null }>("/auth/signup"),
    retry: false,
  });
  const [username, setUsername] = useState("");
  const [debounced, setDebounced] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (pending.data?.suggestion && !username) setUsername(pending.data.suggestion);
  }, [pending.data?.suggestion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setDebounced(normalizeUsername(username)), 300);
    return () => clearTimeout(t);
  }, [username]);

  const check = useQuery({
    queryKey: ["username-check", debounced],
    queryFn: () =>
      api<{ ok: true; username: string } | { ok: false; reason: string }>(`/auth/username-check?u=${encodeURIComponent(debounced)}`),
    enabled: debounced.length > 0,
  });

  if (pending.isError) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <Logo size={40} />
          <p className="alert">Your sign-in expired. Please start again.</p>
          <a className="btn btn-primary" href="/login">Back to sign in</a>
        </div>
      </main>
    );
  }

  const name = normalizeUsername(username);
  const current = check.data && debounced === name ? check.data : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api("/auth/signup", { method: "POST", body: json({ username: name }) });
      await qc.invalidateQueries({ queryKey: ["session"] });
      navigate("/", { replace: true });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? (REASONS[err.message] ?? err.message) : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={submit}>
        <Logo size={40} />
        <h1>Choose your address</h1>
        <p className="muted">
          Signed in as <strong>{pending.data?.email ?? "…"}</strong>. Mail you send from it goes straight into your notes.
        </p>
        <label className="field">
          <span>Username</span>
          <div className="address-input">
            <input
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={30}
              aria-describedby="username-help"
            />
            <span className="suffix">@ccnote.me</span>
          </div>
        </label>
        <p id="username-help" className={current && !current.ok ? "field-error" : "muted small"}>
          {current && !current.ok
            ? REASONS[current.reason]
            : current?.ok
              ? `${formatAddress(name)} is available.`
              : "This becomes your email address and can't be changed later."}
        </p>
        {submitError && <p className="alert">{submitError}</p>}
        <button className="btn btn-primary" disabled={!current?.ok || submitting}>
          {submitting ? "Creating…" : "Create my address"}
        </button>
      </form>
    </main>
  );
}
